// src/components/StockOutItems.js
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaSearch, // <--- added to fix the eslint error
} from 'react-icons/fa';
import StockOutFormModal from './StockOutFormModal'; // Import the modal component
import StockOutViewModal from './StockOutViewModal';
import PDFDesigner from './pdfDesigner';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const StockOutItems = () => {
  // === Adopt StockIn (Realist Beige / Kilim Beige) color tokens mapped to existing names ===
  // kept the same variable names used through the layout so no layout changes required
  const COLORS = {
    CLOUD: '#F5EFE6',   // page background (mapped from StockIn.pageBg)
    BABY: '#F0ECE2',    // header / table header / primary tint (mapped from StockIn.headerBg)
    BABY1: '#B88A5A',   // lighter accent used for active pagination / primary buttons (mapped from StockIn.primary)
    DIVIDER: '#E6D9CC', // divider color (mapped from StockIn.border)
    BORDER: '#E6D9CC',  // card & input border (same as DIVIDER)
    TEXT: '#3C2F28',    // primary text (mapped from StockIn.textDark)
    MUTED: '#6E584A',   // secondary text (mapped from StockIn.textMuted)
  };

  // --- THEME CSS VARIABLES FOR SIDEBAR & GLOBAL THEME (Stock Out page) ---
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER,
      '--sidebar-bg': '#ffffff',
      '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1,
      '--accent-hover': '#A27246', // StockIn.primaryHover
      '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT,
      '--text-muted': COLORS.MUTED,
      '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3',
      '--search-icon': COLORS.MUTED,
      '--hover-gray': '#F6EFE4' // StockIn.badgeBg
    };

    Object.entries(themeVars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });

    // Intentionally not removing them on unmount so the next page can override on mount.
  }, []); 
  // -------------------------------------------------------------------------

  // ----------------- Inject scoped CSS overrides mapped to StockIn tokens -----------------
  useEffect(() => {
    const styleId = 'stockout-currencymaster-overrides';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;

    style.innerHTML = `
      /* Scope overrides to the StockOut page root */
      .stockout-root .rounded-md,
      .stockout-root .rounded-lg,
      
      .stockout-root .rounded-xl,
      .stockout-root .rounded-sm,
      

      /* Force sharp inputs / selects / buttons used in this component */
      .stockout-root input[type="text"],
      .stockout-root select,
      .stockout-root .shadow,
      .stockout-root .shadow-sm,
      .stockout-root .rounded-md,
      

      /* Make cards and table containers sharp and use subtle border */
      
      .stockout-root .shadow-lg,
      .stockout-root .shadow-sm,
      .stockout-root .shadow {
        box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
      }

      /* Pagination active block styling (sharp) */
      

      /* Ensure sticky header shadow contrast when scrolled */
      .stockout-root .sticky.top-0.shadow-lg {
        box-shadow: 0 1px 0 rgba(15,23,42,0.06) !important;
      }

      /* Force table header corners to be sharp */
      .stockout-root table thead th {
        border-radius: 0 !important;
      }

      /* Keep hover backgrounds subtle and crisp */
      .stockout-root tr:hover {
        background-color: #faf9f8 !important;
      }

      /* Map common Tailwind utilities used in this file to theme values */
      .stockout-root { background: ${COLORS.CLOUD} !important; }

      /* Panels / cards */
      .stockout-root .bg-white { background: #ffffff !important; }
      .stockout-root .bg-slate-50 { background: #F6EFE4 !important; } /* badgeBg */
      .stockout-root .bg-slate-100 { background: ${COLORS.BABY} !important; }

      /* Text colors */
      .stockout-root .text-slate-900, .stockout-root .text-slate-800 { color: ${COLORS.TEXT} !important; }
      .stockout-root .text-slate-700, .stockout-root .text-slate-600, .stockout-root .text-slate-500 { color: ${COLORS.MUTED} !important; }

      /* Borders */
      .stockout-root .border-slate-200 { border-color: ${COLORS.BORDER} !important; }

      /* Primary buttons (map blue to kilim beige) */
      .stockout-root .bg-blue-500 { background-color: ${COLORS.BABY1} !important; }
      .stockout-root .bg-blue-500:hover { background-color: #A27246 !important; } /* primaryHover */
      .stockout-root .hover\\:bg-blue-400:hover { background-color: #A27246 !important; }
      .stockout-root .text-white { color: #fff !important; }

   
     

      /* Hover / subtle backgrounds */
      .stockout-root .hover\\:bg-slate-50:hover { background-color: #faf9f8 !important; }

      /* Make table row backgrounds plain white */
      .stockout-root table tbody tr {
        background: #FFFFFF !important;
      }
      .stockout-root table thead {
        background: ${COLORS.BABY} !important;
      }

      /* Ensure input/selects use theme border and white bg */
      .stockout-root input[type="text"], .stockout-root select {
        background: #ffffff !important;
        border: 1px solid ${COLORS.BORDER} !important;
        color: ${COLORS.TEXT} !important;
      }

      /* ---------- TABLE DIVIDERS ---------- */
      .stockout-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .stockout-root table thead th { background-clip: padding-box; }
      /* draw a full-width 1px line for every row using :after so it spans the whole row including the actions column */
      .stockout-root table tbody tr { position: relative; }
      .stockout-root table tbody tr::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        background: ${COLORS.DIVIDER}; /* divider color */
        bottom: 0;
        pointer-events: none;
      }
      .stockout-root table tbody td { border-bottom: none; position: relative; z-index: 1; background-clip: padding-box; }
      .stockout-root table thead th.sticky { position: sticky; top: 0; z-index: 20; }

      /* ---------- Rounded badges (status / inventory) ---------- */
      .stockout-root .status-badge,
      .stockout-root .inventory-badge {
        border-radius: 9999px !important;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        line-height: 1;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
        box-shadow: none !important;
        border: none !important;
      }

      /* ---------- Force sidebar profile avatar circular on StockOut page ---------- */
      .stockout-root .sidebar .profile,
      .stockout-root .sidebar .profile-icon,
      .stockout-root .sidebar .profile-img,
      .stockout-root .sidebar .avatar,
      .stockout-root [data-sidebar] .profile,
      .stockout-root [data-sidebar] .avatar,
      .stockout-root aside .profile,
      .stockout-root aside .avatar,
      .stockout-root aside img.profile-img {
        border-radius: 9999px !important;
        overflow: hidden !important;
      }
    `;

    document.head.appendChild(style);
    // no cleanup so next mount can override if needed
  }, []); 
  // ---------------------------------------------------------------------------------

  // Force-apply avatar circular style if Sidebar renders with different classnames or inline styles
  useEffect(() => {
    const applyAvatarStyles = () => {
      // possible sidebar containers
      const sidebarSelectors = [
        '.stockout-root aside',
        '.stockout-root nav',
        '.stockout-root .sidebar',
        '.stockout-root [data-sidebar]',
        '.stockout-root aside .sidebar',
      ];
      let sidebarEl = null;
      for (const sel of sidebarSelectors) {
        sidebarEl = document.querySelector(sel);
        if (sidebarEl) break;
      }
      if (!sidebarEl) return;

      // Find images inside sidebar
      const imgs = sidebarEl.querySelectorAll('img');
      imgs.forEach(img => {
        // apply circular styling
        img.style.borderRadius = '9999px';
        img.style.overflow = 'hidden';
        img.style.objectFit = 'cover';
        // optionally ensure width/height remain same as original, don't force size
      });

      // Find divs/spans used as avatar (initials / background)
      const avatarCandidates = sidebarEl.querySelectorAll('[class*="profile"], [class*="avatar"], [data-avatar], .user-avatar, .profile-icon, .profile-img, .avatar');
      avatarCandidates.forEach(el => {
        // If it's not an <img>, we still force the rounding and overflow
        if (el && el.nodeType === 1) {
          el.style.borderRadius = '9999px';
          el.style.overflow = 'hidden';
          // if element has background image or bg color, this will round it visually
        }
      });
    };

    // Initial try
    applyAvatarStyles();

    // Observe sidebar for dynamic updates (reapply when sidebar content changes)
    const root = document.querySelector('.stockout-root');
    if (!root) return;
    const sidebarNode = root.querySelector('aside, nav, .sidebar, [data-sidebar]');
    const observerTarget = sidebarNode || root;
    const mo = new MutationObserver(() => {
      // small debounce
      setTimeout(() => applyAvatarStyles(), 50);
    });
    mo.observe(observerTarget, { childList: true, subtree: true, attributes: true });
    // cleanup
    return () => mo.disconnect();
  }, []);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [inventoryFilter, setInventoryFilter] = useState('All');
  const [stockOuts, setStockOuts] = useState([]);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedStockOutForPDF, setSelectedStockoutForPDF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStockOut, setSelectedStockOut] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalStockOuts: 0,
    withInventoryFlag: 0,
    withoutInventoryFlag: 0,
    createdThisMonth: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [apiError, setApiError] = useState(null);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Fetch current user info
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Fetch stock out records when currentUser available
  useEffect(() => {
    if (currentUser) {
      fetchStockOuts();
    }
  }, [currentUser]);

  const downloadStockoutPDF = async (stockoutNumber) => {
    if (!stockoutNumber) {
      console.error('No StockOut ID provided for PDF download');
      return;
    }

    try {
      console.log(`Requesting PDF for SO: ${stockoutNumber}`);
      const response = await axios.get(`${API_URL}/designs/Stock%20out/pdf/stockout/${stockoutNumber}`, {
        responseType: 'arraybuffer'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Stk_out_${stockoutNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(`Failed to download PDF: ${error.message}`);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
       console.log('Fetching current user with token:', token ? 'Token found' : 'No token');

      const response = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching current user:', response.status, errorData);
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      const userData = await response.json();
      console.log('Current user data:', userData);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // fallback: try extracting from token
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setCurrentUser({
            UserID: payload.userId || payload.sub,
            UserName: payload.email || payload.username
          });
           console.log('User info from token:', payload);
        }
      } catch (tokenError) {
        console.error('Error decoding token:', tokenError);
        setApiError('Authentication error. Please log in again.');
      }
    }
  };

  const fetchStockOuts = async () => {
    try {
      setLoading(true);
      setApiError(null);

      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) 
        throw new Error('Authentication token not found. Please log in again.');

      const response = await fetch(`${API_URL}/stockout`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server response error:', response.status, errorData);
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched stock out data:', data);
      setStockOuts(Array.isArray(data) ? data : []);
      calculateStats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching stock out records:', error);
      setApiError(error.message || 'Failed to fetch stock out records');
      setStockOuts([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (stockOutData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let totalStockOuts = stockOutData.length;
    let withInventoryFlag = 0;
    let withoutInventoryFlag = 0;
    let createdThisMonth = 0;

    stockOutData.forEach(stockOut => {
      if (stockOut.InventoryFlag === true || stockOut.InventoryFlag === 1) {
        withInventoryFlag++;
      } else {
        withoutInventoryFlag++;
      }

      const stockOutDate = new Date(stockOut.StockOutDate);
      if (stockOutDate.getMonth() === currentMonth && stockOutDate.getFullYear() === currentYear) {
        createdThisMonth++;
      }
    });

    setStats({
      totalStockOuts,
      withInventoryFlag,
      withoutInventoryFlag,
      createdThisMonth
    });
  };
   const handleLogout = () => {
    navigate('/login');
  };

  const handleDownloadCSV = () => {
    let csvContent = "Stock Out ID,Date,Stock Out By,Total Quantity,Note,Status,Inventory Flag\n";
    stockOuts.forEach(stockOut => {
      const statusText = stockOut.Status === 1 || stockOut.Status === true ? 'Active' : 'Inactive';
      const inventoryFlag = stockOut.InventoryFlag === 1 || stockOut.InventoryFlag === true ? 'Yes' : 'No';
      csvContent += `${stockOut.StockOutID},${stockOut.StockOutDate},${stockOut.StockOutUserName},${stockOut.TotalQuantity},${stockOut.Note || ''},${statusText},${inventoryFlag}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'stock_out_records.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreate = () => {
    setSelectedStockOut(null);
    setShowModal(true);
  };

  const handleEdit = (stockOut) => {
    setSelectedStockOut(stockOut);
    setShowModal(true);
  };

  const handleView = (stockOut) => {
    setSelectedStockOut(stockOut);
    setViewModalOpen(true);
  };

  const handleDelete = async (stockOutId) => {
    if (!window.confirm(`Are you sure you want to delete Stock Out ${stockOutId}?`)) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/stockout/${stockOutId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      fetchStockOuts();
      alert('Stock out record deleted successfully');
    } catch (error) {
      console.error('Error deleting stock out record:', error);
      alert(`Error deleting stock out record: ${error.message}`);
    }
  };

  const handleModalSubmit = () => {
    fetchStockOuts();
  };

  // Toggle Stock Out status
  const toggleStockOutStatus = async (stockOutId, currentStatus) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      const getResponse = await fetch(`${API_URL}/stockout/${stockOutId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!getResponse.ok) {
        throw new Error('Failed to fetch stock out details');
      }

      const stockOutData = await getResponse.json();
      const newStatus = currentStatus ? 0 : 1;

      const response = await fetch(`${API_URL}/stockout/${stockOutId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...stockOutData,
          Status: newStatus
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      fetchStockOuts();
      alert(`Stock out record ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating stock out status:', error);
      alert(`Error updating stock out status: ${error.message}`);
    }
  };

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'StockOutID':
        case 'StockOutUserName':
          aValue = (a[sortConfig.key] || '').toString().toLowerCase();
          bValue = (b[sortConfig.key] || '').toString().toLowerCase();
          break;
        case 'StockOutDate':
          aValue = new Date(a[sortConfig.key] || 0);
          bValue = new Date(b[sortConfig.key] || 0);
          break;
        case 'TotalQuantity':
          aValue = Number(a[sortConfig.key] || 0);
          bValue = Number(b[sortConfig.key] || 0);
          break;
        case 'Status':
        case 'InventoryFlag':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter stock out records
  const filteredStockOuts = stockOuts.filter(stockOut => {
    const matchesSearch =
      (stockOut.StockOutID || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      (stockOut.StockOutUserName || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      (stockOut.Note || '').toString().toLowerCase().includes(searchTerm.toLowerCase());

    const isActive = stockOut.Status === 1 || stockOut.Status === true;
    const matchesStatus = status === 'All' ||
      (status === 'Active' && isActive) ||
      (status === 'Inactive' && !isActive);

    const hasInventoryFlag = stockOut.InventoryFlag === 1 || stockOut.InventoryFlag === true;
    const matchesInventoryFlag = inventoryFilter === 'All' ||
      (inventoryFilter === 'Yes' && hasInventoryFlag) ||
      (inventoryFilter === 'No' && !hasInventoryFlag);

    return matchesSearch && matchesStatus && matchesInventoryFlag;
  });

  const sortedStockOuts = getSortedData(filteredStockOuts);

  // Pagination logic
  const totalPages = Math.ceil(sortedStockOuts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedStockOuts.slice(startIndex, endIndex);

  useEffect(() => {
    console.log('Current page:', currentPage);
    console.log('Items per page:', itemsPerPage);
    console.log('Total items:', sortedStockOuts.length);
    console.log('Current items:', currentItems.length);
  }, [currentPage, sortedStockOuts, currentItems]);

  // Render sort arrows
  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';

    return (
      <span className="ml-1 inline-flex flex-col">
        <span className={`text-xs ${isAsc ? 'text-slate-900 font-bold' : 'text-slate-500'}`}>
          <FaSortUp />
        </span>
        <span className={`text-xs -mt-1 ${isDesc ? 'text-slate-900 font-bold' : 'text-slate-500'}`} style={{ marginTop: '-0.45rem' }}>
          <FaSortDown />
        </span>
      </span>
    );
  };

  // Pagination helper
  const handlePageChange = (page) => setCurrentPage(page);

  // Render pagination buttons (styled to match StockIn tokens)
  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className={`p-2 transition-all duration-200`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={currentPage === 1 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: COLORS.TEXT }}
        aria-label="Previous"
      >
        <FaChevronLeft className="w-4 h-4" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      const isActive = currentPage === i;
      pages.push(
        <button
          key={i}
          className={`px-3 py-1 font-medium transition-all duration-200  ${isActive ? '' : 'hover:bg-gray-100'}`}
          onClick={() => handlePageChange(i)}
          style={isActive ? { backgroundColor: COLORS.BABY1, color: '#fff' } : { color: COLORS.TEXT }}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className={`p-2 transition-all duration-200`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        style={currentPage === totalPages || totalPages === 0 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: COLORS.TEXT }}
        aria-label="Next"
      >
        <FaChevronRight className="w-4 h-4" />
      </button>
    );

    return pages;
  };

  // Permissions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: COLORS.CLOUD }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto" style={{ borderTopColor: COLORS.BABY, borderBottomColor: COLORS.BABY }}></div>
          <p className="mt-4" style={{ color: COLORS.MUTED }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex stockout-root">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>

        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Stock Out Records</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Manage your stock out records here.</p>
              {currentUser && (
                <p className="text-sm mt-1" style={{ color: COLORS.MUTED }}>
                  Logged in as: {currentUser.UserName} (ID: {currentUser.UserID})
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-2 px-4 py-2 bg-white border font-medium transition-all duration-200 shadow-sm"
                style={{ borderColor: COLORS.BORDER, color: COLORS.TEXT }}
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
                  style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#A27246'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
                >
                  <FaPlus className="w-4 h-4" />
                  Create New
                </button>
              )}
              <button
                onClick={() => { if (currentItems[0]) { setSelectedStockoutForPDF(currentItems[0]); setIsPDFDesignerOpen(true); } }}
                className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
                style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#A27246'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
              >
                <FaEdit className="w-4 h-4" /> Design PDF
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {apiError && (
          <div className="mb-6 p-4 bg-red-50 border" style={{ borderColor: COLORS.BORDER }}>
            <p className="text-sm" style={{ color: '#b91c1c' }}>{apiError}</p>
            <div className="mt-2 flex gap-2">
              <button onClick={fetchStockOuts} className="px-3 py-1" style={{ backgroundColor: '#ef4444', color: '#fff' }}>Try again</button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Total Stock Outs</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.totalStockOuts}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-100" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>With Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.withInventoryFlag}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-teal-50" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Without Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.withoutInventoryFlag}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-amber-50" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Created This Month</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.createdThisMonth}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-100" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: COLORS.MUTED }} />
                <input
                  type="text"
                  placeholder="Search Stock Out ID, Stock Out By, Note, etc."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                  style={{ borderColor: COLORS.BORDER }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Inventory Flag</label>
              <select
                value={inventoryFilter}
                onChange={(e) => setInventoryFilter(e.target.value)}
                className="px-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <button
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm"
              onClick={() => fetchStockOuts()}
              style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#A27246'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Stock Out Records</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-500">
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          <div className="h-full overflow-x-auto">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Loading stock out records...</p>
              </div>
            ) : sortedStockOuts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500">No stock out records found</p>
              </div>
            ) : (
              <table className="w-full custom-table" style={{ borderCollapse: 'separate' }}>
                <thead className="text-xs font-semibold text-left uppercase" style={{ backgroundColor: COLORS.BABY, color: COLORS.TEXT, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('StockOutID')}>
                      <div className="flex items-center">Stock Out ID {renderSortArrows('StockOutID')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('StockOutDate')}>
                      <div className="flex items-center">Date {renderSortArrows('StockOutDate')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('StockOutUserName')}>
                      <div className="flex items-center">Stock Out By {renderSortArrows('StockOutUserName')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('TotalQuantity')}>
                      <div className="flex items-center">Total Quantity {renderSortArrows('TotalQuantity')}</div>
                    </th>
                    <th className="px-4 py-3 text-left">Note</th>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('Status')}>
                      <div className="flex items-center">Status {renderSortArrows('Status')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer text-left" onClick={() => handleSort('InventoryFlag')}>
                      <div className="flex items-center">Inventory Flag {renderSortArrows('InventoryFlag')}</div>
                    </th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentItems.map((stockOut) => {
                    const isActive = stockOut.Status === 1 || stockOut.Status === true;
                    const statusText = isActive ? 'Active' : 'Inactive';
                    const hasInventoryFlag = stockOut.InventoryFlag === 1 || stockOut.InventoryFlag === true;
                    const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';

                    return (
                      <tr key={stockOut.StockOutID}>
                        <td className="px-4 py-3 font-medium uppercase" style={{ color: COLORS.TEXT  , fontSize: '0.825rem' , fontWeight:'500'}}>{stockOut.StockOutID}</td>
                        <td className="px-4 py-3 uppercase" style={{ color: COLORS.MUTED , fontSize: '0.825rem' }}>{stockOut.StockOutDate ? new Date(stockOut.StockOutDate).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 uppercase" style={{ color: COLORS.MUTED , fontSize: '0.825rem'  }}>{stockOut.StockOutUserName || '-'}</td>
                        <td className="px-4 py-3" style={{ color: COLORS.MUTED , fontSize: '0.825rem'  }}>{Math.abs(stockOut.TotalQuantity || 0)}</td>
                        <td className="px-4 py-3 uppercase" style={{ color: COLORS.MUTED , fontSize: '0.825rem' }}>{stockOut.Note || '-'}</td>
                        <td className="px-4 py-3">
                          <button
                            className={`  rounded-full ${isActive ? 'stock-status bg-teal-50 font-bold text-teal-700 hover:bg-teal-100' : 'stock-status-in bg-amber-50 font-bold text-amber-700 hover:bg-amber-100 '}`}
                            onClick={() => toggleStockOutStatus(stockOut.StockOutID, isActive)}
                            title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                            
                          >
                          
                            {statusText}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inventory-badge inline-block px-3 py-1 text-xs font-semibold uppercase" style={{ background: '#F6EFE4', color: hasInventoryFlag ? COLORS.TEXT : COLORS.MUTED }}>
                            {inventoryFlagText}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center space-x-4" style={{ color: COLORS.MUTED }}>
                            {canView && (
                              <button onClick={() => handleView(stockOut)} title="View" style={{ color: COLORS.TEXT }} className="hover:opacity-90">
                                <FaEye />
                              </button>
                            )}
                            {canEdit && (
                              <button onClick={() => handleEdit(stockOut)} title="Edit" className="hover:opacity-90" style={{ color: COLORS.BABY1 }}>
                                <FaEdit />
                              </button>
                            )}
                            <button onClick={() => downloadStockoutPDF(stockOut.StockOutID)} title="Download PDF" className="hover:opacity-90" style={{ color: COLORS.TEXT }}>
                              <FaDownload />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Stock Out Form Modal */}
        <StockOutFormModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialData={selectedStockOut}
          isEdit={!!selectedStockOut}
          currentUser={currentUser}
        />

        <StockOutViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          stockOutId={selectedStockOut}
        />

        {/* PDF Designer Modal */}
        {isPDFDesignerOpen && selectedStockOutForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="Stock Out"
            sampleData={{
              StockOutID: selectedStockOutForPDF.StockOutID || selectedStockOutForPDF.stockOutId || '-',
              StockOutDate: selectedStockOutForPDF.StockOutDate ? new Date(selectedStockOutForPDF.StockOutDate).toLocaleDateString() : '-',
              StockOutByUserID: selectedStockOutForPDF.StockOutByUserID || selectedStockOutForPDF.stockOutByUserId || '-',
              StockOutUserName: selectedStockOutForPDF.StockOutUserName || selectedStockOutForPDF.stockOutUserName || '-',
              BranchCode: selectedStockOutForPDF.BranchCode || selectedStockOutForPDF.branchCode || '-',
              BranchName: selectedStockOutForPDF.BranchName || selectedStockOutForPDF.branchName || '-',
              Status: selectedStockOutForPDF.Status === 1 || selectedStockOutForPDF.Status === true ? 'Active' : 'Inactive',
              InventoryFlag: selectedStockOutForPDF.InventoryFlag === 1 || selectedStockOutForPDF.InventoryFlag === true ? 'Yes' : 'No',
              TotalQuantity: selectedStockOutForPDF.TotalQuantity || selectedStockOutForPDF.totalQuantity || 0,
              Note: selectedStockOutForPDF.Note || selectedStockOutForPDF.note || '-',
              Items: (selectedStockOutForPDF.Items || []).map(item => ({
                ItemCode: item.ItemCode || item.itemCode || '-',
                ItemName: item.ItemName || item.itemName || '-',
                UOM: item.UOM || item.uom || '-',
                QuantityMinus: item.QuantityMinus || item.quantityMinus || 0,
                Rate: item.Rate || item.rate || 0,
                Value: item.Value || item.value || 0,
                Location: item.Location || item.location || '-',
                Reason: item.Reason || item.reason || '-',
              })),
              Summary: {
                TotalQuantity: selectedStockOutForPDF.TotalQuantity || selectedStockOutForPDF.totalQuantity || 0,
                TotalValue: (selectedStockOutForPDF.Items || []).reduce((sum, i) => sum + (i.Value || i.value || 0), 0)
              }
            }}
            availableFields={{
              Header: [
                "StockOutID", "StockOutDate", "StockOutByUserID", "StockOutUserName",
                "BranchCode", "BranchName", "Status", "InventoryFlag",
                "TotalQuantity", "Note"
              ],
              Items: [
                "ItemCode", "ItemName", "UOM", "QuantityMinus",
                "Rate", "Value", "Location", "Reason"
              ],
              Summary: [
                "TotalQuantity", "TotalValue"
              ]
            }}
          />
        )}

      </main>
    </div>
  );
};

export default StockOutItems;
